import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InjectedFormProps, reduxForm } from 'redux-form';
import * as R from 'ramda';
import { sortBy } from 'lodash';
import {
  Switch,
  Route,
  useLocation,
  useParams,
  useHistory,
  useRouteMatch,
} from 'react-router';

import { TopNavigationBar } from '@common/components/navigation-bar';
import { Button } from '@common/components/button';
import { Container } from '@common/components/container';
import { SubmissionsContainer } from '../submissions';
import { FormForm } from '../../forms/form';
import { FormSettings } from './form-settings';
import { FormQuestions } from './form-questions';
import { useIsAvailableInPlanPackage } from '@common/hooks/use-is-available-in-plan-package';
import * as formsSelector from '../../selectors';
import { useAppSelector, useFormValues, useThunkDispatch } from '@common/hooks';
import { FormIcon } from '../../components/form-icon';
import { predicatesToPayload } from '@utils/predicates';
import { EPlanPackageConfig } from '@common/definitions';
import { EComponentTypes } from '@modules/learning/definitions';
import {
  fetchForm,
  createForm,
  updateForm,
  saveDraft,
  FormPayload,
  clearForm,
} from '../../actions';
import {
  createQuestion as createQuestionAction,
  deleteQuestion as deleteQuestionAction,
  duplicateScreen,
  saveScreenOrder,
  updateScreenOrder,
} from '@modules/forms/actions';
import resetDraft from '@modules/learning/actions/reset-draft';

import { AlertService } from '@common/services/alert';
import { useQueryParam } from '@common/hooks/url';
import { Question, ScreenItem } from '@modules/learning/types/objects';
import swap from '@common/utils/swap';
import isFormValidById from '@common/utils/is-form-valid-by-id';
import Spinner from '@common/components/spinner';

import { createInitialValues } from './utils';
import { FormStatusInput } from '@modules/forms/components/form-status-input';
import './edit-form.scss';

type Params = {
  id?: string;
  screenId?: string;
};

type FormData = FormPayload & {
  activate_status: boolean;
};

type Props = InjectedFormProps<FormData>;

const EditFormContainer = ({
  initialize,
  change,
  handleSubmit,
  submitting,
}: Props) => {
  const { t } = useTranslation();
  const params = useParams<Params>();
  const history = useHistory();
  const thunkDispatch = useThunkDispatch();
  const back = useQueryParam('back');
  const { pathname, search } = useLocation();
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const values = useFormValues<FormData>('edit-form');

  const selectedForm = useAppSelector((state) => {
    return params.id ? formsSelector.getForm(state, params.id) : null;
  });

  const moderators = useAppSelector((state) => {
    return params.id ? formsSelector.getFormModerators(state, params.id) : null;
  });

  const componentsForm = useAppSelector((state) => state.form);
  const isFormValid = useMemo(
    () => isFormValidById(componentsForm, 'question-'),
    [componentsForm]
  );

  const { isAvailable: canCreateFileUploadQuestion, showUpgradeModal } =
    useIsAvailableInPlanPackage(EPlanPackageConfig.FORMS_SPECIAL_INPUT);

  useEffect(() => {
    // If form isn't in the store yet
    if (params.id && (!selectedForm || !selectedForm?.screen_ids)) {
      (async () => {
        setFormLoading(true);
        const { item } = await thunkDispatch(fetchForm(params.id!));
        initialize(createInitialValues(item as FormPayload));
        setFormLoading(false);
      })();
    }
  }, [selectedForm, thunkDispatch, params.id, initialize]);

  useEffect(() => {
    return () => {
      thunkDispatch(clearForm(params.id!));
    };
  }, []);

  // Reset draft when leaving this page
  // @ts-expect-error
  useEffect(() => () => thunkDispatch(resetDraft()), []);

  const onSubmit = handleSubmit(async (newValues) => {
    const omit = ['response_statuses', 'activate_status', 'status', 'audience'];
    const payload = R.omit(omit, newValues) as FormPayload;

    if (newValues.audience) {
      payload.audience = {
        predicate_type: newValues.audience.predicate_type,
        // @ts-expect-error
        predicates: predicatesToPayload(newValues.audience.predicates),
      };
    }

    if (selectedForm) {
      if (selectedForm.status === 'live')
        payload.settings = R.omit(['is_anonymous'], newValues.settings);
      payload.status = newValues.status;

      // if user de-selected the "Enable status" toggle then we must override
      // the existing response_statuses with an empty array otherwise
      // the activate_status toggle will have inconsistent state as it relies
      // on responses length for initial value setting when the form is
      // first rendered
      // see https://flex-appeal.atlassian.net/browse/PD-8524
      const statusesWereEnabled = selectedForm?.response_statuses?.length;
      if (statusesWereEnabled && !newValues.activate_status) {
        payload.response_statuses = [];
      }

      // Existing form
      try {
        await thunkDispatch(saveDraft(selectedForm.id));
        await thunkDispatch(updateForm(selectedForm.id, payload));

        return AlertService.success(
          t('forms:updated_form', { title: payload.title })
        );
      } catch (response) {
        // @ts-expect-error
        return AlertService.forStatus(response.status_code, {
          warning: t('forms:update_form_warning'),
          error: t('forms:update_form_error'),
        });
      }
    }

    try {
      const { item } = await thunkDispatch(createForm(payload));

      history.replace(`/admin/forms/${item.id}/edit/screens${search}`);
      return AlertService.success(
        t('forms:created_form', { title: payload.title })
      );
    } catch (response) {
      // @ts-expect-error
      return AlertService.forStatus(response.status_code, {
        warning: t('forms:create_form_warning'),
        error: t('forms:create_form_error'),
      });
    }
  });

  const fns = {
    createQuestion: async (question: Question) => {
      if (
        !canCreateFileUploadQuestion &&
        question.type === EComponentTypes.QUESTION_FILE_UPLOAD
      ) {
        showUpgradeModal();
      } else {
        const payload = question ? { components: [question] } : {};
        const { screen } = await thunkDispatch(
          createQuestionAction(selectedForm?.id!, payload)
        );
        history.push(
          `/admin/forms/${selectedForm?.id}/edit/screens/${screen.id}`
        );
      }
    },
    openQuestion: (id: string) => {
      history.push(`/admin/forms/${selectedForm?.id}/edit/screens/${id}`);
    },
    deleteQuestion: async (id: string) => {
      await thunkDispatch(deleteQuestionAction(selectedForm?.id!, id));
      if (id === params.screenId) {
        const currentIndex = selectedForm?.screen_ids?.indexOf(id) || 0;
        const newScreenId =
          selectedForm?.screen_ids?.[currentIndex ? currentIndex - 1 : 1];
        history.push(
          `/admin/forms/${selectedForm?.id}/edit/screens/${newScreenId}`
        );
      }
    },
    duplicateQuestion: async (screen: ScreenItem) => {
      await thunkDispatch(duplicateScreen(selectedForm?.id!, screen.id));
    },
    orderQuestions: (id: string, index: number) => {
      if (!selectedForm || !selectedForm.screens) return;
      const order = sortBy(selectedForm.screens, ['index']).map(
        (screen) => screen.id
      );
      const newOrder = swap(order, index, order.indexOf(id));
      thunkDispatch(updateScreenOrder(selectedForm.id, newOrder));
    },
    saveOrderQuestions: async (order: string[]) => {
      try {
        await thunkDispatch(saveScreenOrder(selectedForm?.id!, order));
        AlertService.success(t('survey:survey_order_alert_success'));
      } catch (e: any) {
        if (e && typeof e.status_code === 'number') {
          AlertService.forStatus(e.status_code, {
            warning: t('survey:survey_order_alert_warning'),
            error: t('survey:survey_order_alert_error'),
          });
        }
      }
    },
  };

  // ONE-337 : Check if the current route is submission details page route.
  // So we dont change the route to /submissions if we are already on /submissions/ID
  const isCurrentPageSubmissionDetails = useRouteMatch(
    '/admin/forms/:id/edit/submissions/:submissionId'
  );

  return (
    <Container name="EditForm">
      {!params.id && (
        <FormForm
          change={change}
          submitting={submitting}
          values={values as FormData}
          onSubmit={onSubmit}
          onClose={() => history.push(back || '/admin/forms')}
        />
      )}
      <TopNavigationBar
        breadcrumbs={[
          { name: t('forms:breadcrumb_forms'), path: '/admin/forms' },
          { name: values.title || t('forms:create_form') },
        ]}
        form={FormForm}
        formProps={{
          onSubmit,
          change,
          values,
          autoOpen: false,
        }}
        title={
          (selectedForm && [<FormIcon form={values} />, values.title]) || (
            <span className="Survey__Title">{t('forms:name_the_form')}</span>
          )
        }
        badge={selectedForm?.status}
        action={
          <>
            <FormStatusInput />
            <Button
              size="large"
              onClick={() => history.push(back || '/admin/forms')}
            >
              {t('forms:go_back')}
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() => onSubmit()}
              disabled={!(params.id && selectedForm) || !isFormValid}
              isLoading={submitting}
            >
              {t('common:save')}
            </Button>
          </>
        }
        tabs={[
          {
            name: t('forms:tab_content'),
            to: `/admin/forms/${params.id}/edit/screens`,
            isActive: (match) => match?.isExact || !!params.screenId,
          },
          {
            name: t('forms:tab_settings'),
            to: `/admin/forms/${params.id}/edit/settings`,
          },
          {
            name: t('forms:tab_submissions'),
            to: isCurrentPageSubmissionDetails
              ? pathname
              : `/admin/forms/${params.id}/edit/submissions`,
            count: selectedForm?.responded_count,
          },
        ]}
      />
      {formLoading ? (
        <Spinner size="large" centered />
      ) : (
        <>
          {params.id && selectedForm && (
            <Switch>
              <Route
                exact
                path={[
                  '/admin/forms/:formId/edit/screens',
                  '/admin/forms/:formId/edit/screens/:screenId',
                ]}
                render={(props) => (
                  <FormQuestions
                    {...props}
                    {...fns}
                    disabled={!isFormValid}
                    form={selectedForm}
                  />
                )}
              />
              <Route exact path="/admin/forms/:id/edit/settings">
                <FormSettings
                  values={values}
                  selectedForm={selectedForm}
                  moderators={moderators}
                />
              </Route>
              <Route
                path={[
                  '/admin/forms/:id/edit/submissions/:submissionId',
                  '/admin/forms/:id/edit/submissions',
                ]}
                render={(props) => <SubmissionsContainer
                  {...props}
                  values={values}
                />}
              />
            </Switch>
          )}
        </>
      )}
    </Container>
  );
};

export default reduxForm<FormData>({
  form: 'edit-form',
  initialValues: createInitialValues(),
})(EditFormContainer);
